<template>
  <FocusLoop :auto-focus="false">
    <div ref="modal_page" class="modal-overlay center" @click.self="close">
      <div ref="modal_window" class="modal-card">
        <div class="modal-header row space-between align-center">
          <span class="font-18 weight-600">{{ $t("modal.newMeeting.start") }}</span>
          <button class="close-btn center" @click="close">
            <s-icon color="grey">close</s-icon>
          </button>
        </div>

        <div class="modal-body col gap-24">
          <div class="info-box row align-start gap-10">
            <s-icon class="info-box__icon" height="18" color="primary">information-outline</s-icon>
            <p class="info-box__text">{{ $t("modal.newMeeting.info") }}</p>
          </div>

          <div class="link-section col gap-10">
            <label class="section-label">{{ $t("modal.newMeeting.ready") }}</label>
            <div class="link-row row align-center gap-10">
              <div class="link-display grow row align-center">
                <span class="link-text" :class="{ 'link-text--dim': linkLoading }">{{ fullLink }}</span>
                <div v-if="linkLoading" class="link-display__spinner row align-center">
                  <VueSpinner size="22" color="var(--primary)" />
                </div>
              </div>
              <button
                class="icon-action center"
                :class="{ copied: linkCopied }"
                :disabled="linkLoading"
                @click="copyLink"
                :title="$t('modal.newMeeting.clipboard')"
              >
                <s-icon height="18" :color="linkCopied ? 'primary' : 'grey'">
                  {{ linkCopied ? 'check' : 'content-copy' }}
                </s-icon>
              </button>
            </div>
          </div>

          <div class="divider" />
          <div class="security-section col gap-10">
            <label class="section-label">{{ $t("modal.newMeeting.two") }}</label>
            <div class="seg-control row">
              <button
                v-for="opt in passwordOptions"
                :key="opt.value"
                class="seg-item center grow"
                :class="{ active: option === opt.value }"
                @click="option = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
            <div
              class="password-extra"
              :class="{ 'password-extra--open': option !== 'one' }"
            >
              <transition name="pw-swap" mode="out-in">
                <div v-if="option === 'two'" key="custom" class="password-extra__pane">
                  <div class="password-extra__input grow">
                    <s-text-field
                      v-model="password"
                      :placeholder="$t('modal.newMeeting.enter_password')"
                      block
                    />
                  </div>
                  <button class="icon-action password-copy-btn center" :class="{ copied: passwordCopied }" @click="copyPassword" :title="$t('modal.newMeeting.clipboard')">
                    <s-icon height="18" :color="passwordCopied ? 'primary' : 'grey'">
                      {{ passwordCopied ? 'check' : 'content-copy' }}
                    </s-icon>
                  </button>
                </div>
                <div
                  v-else-if="option === 'three'"
                  key="generated"
                  class="password-extra__pane password-extra__pane--generated row align-center gap-8"
                >
                  <s-icon height="14" color="primary">lock</s-icon>
                  <span class="password-extra__value grow">{{ password }}</span>
                  <button class="icon-action password-copy-btn center" :class="{ copied: passwordCopied }" @click="copyPassword" :title="$t('modal.newMeeting.clipboard')">
                    <s-icon height="18" :color="passwordCopied ? 'primary' : 'grey'">
                      {{ passwordCopied ? 'check' : 'content-copy' }}
                    </s-icon>
                  </button>
                </div>
              </transition>
            </div>
          </div>

  

          <div class="invite-section">
            <button
              type="button"
              class="invite-toggle row space-between align-center"
              :aria-expanded="inviteExpanded"
              @click="inviteExpanded = !inviteExpanded"
            >
              <span class="row align-center gap-8">
                <span class="section-label invite-toggle__label">{{ $t("modal.newMeeting.invite") }}</span>
                <span class="invite-toggle__optional">{{ $t("modal.newMeeting.inviteOptional") }}</span>
              </span>
              <s-icon
                class="invite-toggle__chevron"
                :class="{ 'invite-toggle__chevron--open': inviteExpanded }"
                height="20"
                color="grey"
              >
                chevron-down
              </s-icon>
            </button>
            <div class="invite-panel" :class="{ 'invite-panel--open': inviteExpanded }">
              <div class="invite-panel__inner">
                <div class="invite-row row gap-10">
                  <div class="grow">
                    <s-text-field
                      v-model="email"
                      :error="message"
                      :placeholder="$t('modal.newMeeting.enter')"
                      block
                      @input="resetSendState"
                      @keyup.enter="invite"
                    />
                  </div>
                  <s-btn
                    height="50"
                    class="invite-send"
                    :class="emailSent ? 'sent-btn' : 'send-btn shade1'"
                    @click="invite"
                  >
                    <s-icon v-if="emailSent" height="16" color="primary">check</s-icon>
                    <span>{{ emailSent ? $t("modal.newMeeting.sent") : $t("modal.newMeeting.send") }}</span>
                  </s-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <s-btn block class="primary white--text join-btn" @click="join">
            <s-icon height="20" color="white">video-plus</s-icon>
            <span>{{ $t("modal.newMeeting.join") }}</span>
          </s-btn>
        </div>
      </div>
    </div>
  </FocusLoop>
</template>

<script setup>
import { gsap } from "gsap";
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import { VueSpinner } from 'vue3-spinners';
import 'vue-toast-notification/dist/theme-bootstrap.css';

const { t } = useI18n();
const store = useStore();
const $toast = useToast();

const password = ref("");
const option = ref("one");
const email = ref("");
const message = ref("");
const error = ref(false);
const emailSent = ref(false);
const linkCopied = ref(false);
const passwordCopied = ref(false);
const inviteExpanded = ref(false);
const modal_page = ref(null);
const modal_window = ref(null);
const meetingLinks = ref({ organizerLink: "", guestLink: "", hash: "" });
const creatingMeeting = ref(false);
const linkUpdating = ref(false);
let refreshTimer = null;

const linkLoading = computed(() => creatingMeeting.value || linkUpdating.value);

const passwordOptions = computed(() => [
  { value: 'one', label: t('atoms.no_password') },
  { value: 'two', label: t('atoms.custom_password') },
  { value: 'three', label: t('atoms.generate_password') },
]);

const meetServer = computed(() => store.state.user.meetServer);
const hashLink = computed(() => store.state.user.hash);
const fallbackLink = computed(() => "https://" + meetServer.value + "/" + hashLink.value);
const fullLink = computed(() => meetingLinks.value.guestLink || fallbackLink.value);
const meetingPassword = computed(() => (option.value === "one" ? "" : password.value));

const close = () => {
  gsap.to(modal_page.value, {
    opacity: 0,
    duration: 0.3,
    ease: "power2.out",
  });
  gsap.to(modal_window.value, {
    scale: 0.7,
    duration: 0.3,
    ease: "power2.out",
    onComplete: () => store.commit("modals/meetingModal", false),
  });
};

const resetSendState = () => {
  if (emailSent.value) emailSent.value = false;
};

const invite = async () => {
  if (!validate()) return;
  const meeting = await ensureMeeting();
  if (!meeting?.hash) {
    $toast.error("Unable to create meeting link. Please try again.");
    return;
  }
  const url = "/api/invite/" + meeting.hash + "/" + email.value;
  const emailAddress = email.value;
  const response = await store.dispatch("auth/invite", url);
  email.value = "";
  if (response.status === "ok") {
    $toast.success(`${t("modal.newMeeting.email_sent")}${emailAddress}`);
    emailSent.value = true;
  } else {
    $toast.error("Failed to send email. Please try again.");
  }
};

const validate = () => {
  if (email.value === "") {
    message.value = t("modal.newMeeting.enter_valid_email");
    error.value = true;
    return false;
  }
  const regexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!regexp.test(email.value.toLowerCase())) {
    message.value = t("modal.newMeeting.enter_valid_email");
    error.value = true;
    return false;
  }
  message.value = "";
  error.value = false;
  return true;
};

const generatePassword = () => {
  let length = 16,
    charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*",
    retVal = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

const ensureMeeting = async (force = false) => {
  if (!force && meetingLinks.value.guestLink) return meetingLinks.value;
  if (creatingMeeting.value) return null;
  if (force) {
    meetingLinks.value = { organizerLink: "", guestLink: "", hash: "" };
  }
  creatingMeeting.value = true;
  try {
    const response = await store.dispatch("user/startMeeting", {
      password: meetingPassword.value,
      title: "Start meeting now!",
    });
    if (response?.status !== "ok" || !response?.guestLink) {
      $toast.error("Unable to create meeting link. Please try again.");
      return null;
    }
    meetingLinks.value = {
      organizerLink: response.organizerLink || "",
      guestLink: response.guestLink || "",
      hash: response.hash || "",
    };
    return meetingLinks.value;
  } finally {
    creatingMeeting.value = false;
  }
};

const refreshMeetingLink = () => {
  if (refreshTimer) clearTimeout(refreshTimer);
  linkUpdating.value = true;
  refreshTimer = setTimeout(async () => {
    try {
      await ensureMeeting(true);
    } finally {
      linkUpdating.value = false;
    }
  }, 250);
};

const join = () => {
  ensureMeeting().then((meeting) => {
    const link = meeting?.organizerLink || meeting?.guestLink;
    if (!link) return;
    store.commit("modals/meetingModal", false);
    window.open(link);
  });
};

const copyLink = () => {
  ensureMeeting().then((meeting) => {
    if (!meeting?.guestLink) return;
    navigator.clipboard.writeText(meeting.guestLink);
    linkCopied.value = true;
    $toast.success(t("modal.newMeeting.clipboard"), {
      position: 'top-right',
      duration: 3000,
    });
    setTimeout(() => { linkCopied.value = false; }, 2000);
  });
};

const copyPassword = () => {
  if (!password.value) return;
  navigator.clipboard.writeText(password.value);
  passwordCopied.value = true;
  $toast.success(t("modal.newMeeting.clipboard"), {
    position: 'top-right',
    duration: 3000,
  });
  setTimeout(() => { passwordCopied.value = false; }, 2000);
};

const passwordInit = () => {
  if (option.value === "one") {
    password.value = "";
  }
  if (option.value === "two") {
    password.value = "";
  }
  if (option.value === "three") {
    password.value = generatePassword();
  }
};

watch(option, () => {
  passwordInit();
  refreshMeetingLink();
});

watch(password, () => {
  if (passwordCopied.value) passwordCopied.value = false;
  if (option.value === "one") return;
  refreshMeetingLink();
});

onMounted(() => {
  passwordInit();
  gsap.from(modal_page.value, {
    opacity: 0,
    duration: 0.3,
    ease: "power2.out",
  });
  gsap.from(modal_window.value, {
    scale: 0.7,
    duration: 0.3,
    ease: "power2.out",
  });
  ensureMeeting();
});

onUnmounted(() => {
  if (refreshTimer) clearTimeout(refreshTimer);
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  z-index: 8;
  backdrop-filter: blur(2px);
}

.modal-card {
  width: 100%;
  max-width: 555px;
  background-color: var(--shade1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.modal-header {
  padding: 24px 28px 0;
}

.icon-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.info-box {
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--primary-light);
  border: 1px solid rgba(64, 152, 117, 0.2);
  border-left: 3px solid var(--primary);
}

.info-box__icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.info-box__text {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 400;
  color: var(--shade7);
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--shade3);
  cursor: pointer;
  transition: background 0.2s;
}

.close-btn:hover {
  background: var(--shade4);
}

.modal-body {
  padding: 20px 28px;
}

.section-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--shade6);
}

.link-section .link-row {
  background: var(--shade3);
  border-radius: 10px;
  padding: 4px 4px 4px 14px;
  min-height: 50px;
}

.link-display {
  position: relative;
  min-width: 0;
  min-height: 42px;
}

.link-display__spinner {
  position: absolute;
  inset: 0;
  justify-content: center;
  pointer-events: none;
}

.link-text--dim {
  opacity: 0.25;
}

.link-text {
  font-size: 13px;
  color: var(--primary);
  line-height: 1.4;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-action {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 8px;
  border: none;
  background: var(--shade1);
  cursor: pointer;
  transition: all 0.2s;
}

.icon-action:hover {
  background: var(--shade2);
}

.icon-action.copied {
  background: var(--primary-light);
  background: color-mix(in srgb, var(--primary) 12%, transparent);
}

.seg-control {
  background: var(--shade3);
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
}

.seg-item {
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: var(--shade6);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.seg-item:hover {
  color: var(--black);
}

.seg-item.active {
  background: var(--shade1);
  color: var(--primary);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  font-weight: 600;
}

.password-extra {
  height: 0;
  overflow: hidden;
  transition: height 0.2s ease;
}

.password-extra--open {
  height: 50px;
}

.password-extra__pane {
  height: 50px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.password-extra__input {
  min-width: 0;
}

.password-extra__pane--generated {
  padding: 0 12px;
  background: var(--primary-light);
  background: color-mix(in srgb, var(--primary) 8%, var(--white) 92%);
  border-radius: 8px;
  box-sizing: border-box;
}

.password-extra__value {
  font-size: 13px;
  opacity: 0.6;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.password-copy-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
}

.divider {
  height: 1px;
  background: var(--shade4);
}

.invite-toggle {
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.invite-toggle__label {
  margin: 0;
}

.invite-toggle__optional {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--shade5);
}

.invite-toggle__chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.invite-toggle__chevron--open {
  transform: rotate(180deg);
}

.invite-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s ease;
}

.invite-panel--open {
  grid-template-rows: 1fr;
}

.invite-panel__inner {
  overflow: hidden;
  min-height: 0;
}

.invite-panel__inner .invite-row {
  padding-top: 10px;
}

.invite-row {
  align-items: center;
}

.invite-send {
  flex-shrink: 0;
}

.send-btn {
  min-width: 88px;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid var(--shade4);
  transition: all 0.2s;
}

.send-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.sent-btn {
  min-width: 88px;
  font-weight: 600;
  font-size: 13px;
  background: var(--primary-light);
  background: color-mix(in srgb, var(--primary) 12%, var(--white) 88%);
  color: var(--primary);
  border: 1px solid var(--primary);
  border: 1px solid color-mix(in srgb, var(--primary) 35%, transparent);
  gap: 4px;
}

.modal-footer {
  padding: 8px 28px 24px;
}

.join-btn {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  letter-spacing: 0.3px;
  transition: all 0.2s;
}

.join-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent);
}

.pw-swap-enter-active,
.pw-swap-leave-active {
  transition: opacity 0.15s ease;
}

.pw-swap-enter-from,
.pw-swap-leave-to {
  opacity: 0;
}

.col {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
}

.center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.align-center {
  align-items: center;
}

.space-between {
  justify-content: space-between;
}

.grow {
  flex-grow: 1;
}

.gap-4 { gap: 4px; }
.gap-8 { gap: 8px; }
.gap-8 { gap: 8px; }
.gap-10 { gap: 10px; }
.gap-24 { gap: 24px; }

.font-13 { font-size: 13px; }
.font-18 { font-size: 18px; }
.weight-600 { font-weight: 600; }
.opacity-60 { opacity: 0.6; }

@media only screen and (max-width: 576px) {
  .modal-card {
    max-width: calc(100vw - 32px);
    border-radius: 14px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .seg-item {
    font-size: 11px;
    padding: 7px 6px;
  }

  .invite-row {
    flex-direction: column;
    gap: 10px;
  }

  .link-text {
    white-space: normal;
    word-break: break-all;
  }

  .invite-row .invite-send {
    width: 100%;
  }
}
</style>
